import mila from 'markdown-it-link-attributes';
import mentionPlugin from './markdownIt/link';
import MarkdownIt from 'markdown-it';

const setImageHeight = inlineToken => {
  const imgSrc = inlineToken.attrGet('src');
  if (!imgSrc) return;
  const url = new URL(imgSrc);
  const height = url.searchParams.get('cw_image_height');
  if (!height) return;
  inlineToken.attrSet('style', `height: ${height};`);
};

const processInlineToken = blockToken => {
  blockToken.children.forEach(inlineToken => {
    if (inlineToken.type === 'image') {
      setImageHeight(inlineToken);
    }
  });
};

const imgResizeManager = md => {
  // Custom rule for image resize in markdown
  // If the image url has a query param cw_image_height, then add a style attribute to the image
  md.core.ruler.after('inline', 'add-image-height', state => {
    state.tokens.forEach(blockToken => {
      if (blockToken.type === 'inline') {
        processInlineToken(blockToken);
      }
    });
  });
};

const createMarkdownInstance = (linkify = true) => {
  return MarkdownIt({
    html: false,
    xhtmlOut: true,
    breaks: true,
    langPrefix: 'language-',
    linkify,
    typographer: true,
    quotes: '“”‘’',
    maxNesting: 20,
  })
    .use(mentionPlugin)
    .use(imgResizeManager)
    .use(mila, {
      attrs: {
        class: 'link',
        rel: 'noreferrer noopener nofollow',
        target: '_blank',
      },
    });
};

const TWITTER_USERNAME_REGEX = /(^|[^@\w])@(\w{1,15})\b/g;
const TWITTER_USERNAME_REPLACEMENT = '$1[@$2](http://twitter.com/$2)';
const TWITTER_HASH_REGEX = /(^|\s)#(\w+)/g;
const TWITTER_HASH_REPLACEMENT = '$1[#$2](https://twitter.com/hashtag/$2)';

// WhatsApp uses a single marker for bold (`*text*`) and strikethrough
// (`~text~`), which Markdown/CommonMark interprets as italic and plain text
// respectively. This converts WhatsApp markup to its Markdown equivalent so the
// dashboard renders WhatsApp messages the same way WhatsApp does. Italic
// (`_text_`) is left untouched since Markdown already renders it as italic.
//
// Single regex, single pass. Alternation tries a backtick run (code) first so
// markers inside code are matched as code and left as-is. Lookbehind keeps the
// character before the marker out of the match so adjacent markup still works;
// the `\S`-hugging groups mirror WhatsApp's rule that a marker must touch a
// non-space character (so `2 * 3` is not turned into bold).
const WHATSAPP_MARKUP_REGEX =
  /(`+)[\s\S]*?\1|(?<=^|[^\w*])\*([^\s*](?:[^*\n]*[^\s*])?)\*(?!\w)|(?<=^|[^\w~])~([^\s~](?:[^~\n]*[^\s~])?)~(?!\w)/gm;

const convertWhatsAppToMarkdown = text => {
  return text.replace(
    WHATSAPP_MARKUP_REGEX,
    (match, codeFence, boldBody, strikeBody) => {
      if (codeFence !== undefined) return match;
      if (boldBody !== undefined) return `**${boldBody}**`;
      if (strikeBody !== undefined) return `~~${strikeBody}~~`;
      return match;
    }
  );
};

class MessageFormatter {
  constructor(
    message,
    isATweet = false,
    isAPrivateNote = false,
    linkify = true,
    withWhatsAppFormatting = false
  ) {
    this.message = message || '';
    this.isAPrivateNote = isAPrivateNote;
    this.isATweet = isATweet;
    this.linkify = linkify;
    this.withWhatsAppFormatting = withWhatsAppFormatting;
    this.md = createMarkdownInstance(linkify);
  }

  formatMessage() {
    let updatedMessage = this.message;
    if (this.isATweet && !this.isAPrivateNote) {
      updatedMessage = updatedMessage.replace(
        TWITTER_USERNAME_REGEX,
        TWITTER_USERNAME_REPLACEMENT
      );
      updatedMessage = updatedMessage.replace(
        TWITTER_HASH_REGEX,
        TWITTER_HASH_REPLACEMENT
      );
    }
    if (this.withWhatsAppFormatting) {
      updatedMessage = convertWhatsAppToMarkdown(updatedMessage);
    }
    return this.md.render(updatedMessage);
  }

  get formattedMessage() {
    return this.formatMessage();
  }

  get plainText() {
    const strippedOutHtml = new DOMParser().parseFromString(
      this.formattedMessage,
      'text/html'
    );
    return strippedOutHtml.body.textContent || '';
  }
}

export default MessageFormatter;
